import axios from 'axios'
import SecureLS from 'secure-ls'

const secureLs = new SecureLS()

const api = axios.create({
  timeout: 120000,
  baseURL: process.env.REACT_APP_API_USER_URL,
})

// Interceptor para tratamento de erros 401 (token expirado)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const excludedRoutes = ['/auth']

    const requestUrl = error.config?.url || ''
    const shouldSkip = excludedRoutes.some((route) => requestUrl.includes(route))

    if (error.response?.status === 401 && !shouldSkip) {
      secureLs.remove('appc-auth-mkplace')
      window.location.href = '/login'
    }

    return Promise.reject(error)
  },
)

export const setAuthorizationHeader = ({ token, isLoggedIn, company, id, jwtToken }) => {
  if (isLoggedIn) {
    let authorizationHeaderValue

    if (jwtToken) {
      authorizationHeaderValue = jwtToken
    } else if (token) {
      authorizationHeaderValue = `Bearer ${token}`
    }
    api.defaults.headers['Authorization'] = authorizationHeaderValue
    api.defaults.headers['company'] = company
    api.defaults.headers['id'] = id
  } else {
    delete api.defaults.headers['Authorization']
  }
}

export const signup = (body) => {
  return api.post('/api/1.0/users', body)
}

export const activate = (token) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.post('/api/1.0/users/token/' + token)
}

export const login = (creds) => {
  return api.post('/api/1.0/auth', { ...creds })
}

export const logout = () => {
  return api.post('/api/1.0/logout')
}

export const changeLanguage = (language) => {
  api.defaults.headers['accept-language'] = language
}

export const getUsers = (page = 0, size = 10, text = '', companyId = null) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  const params = {
    page,
    size,
    text,
  }

  // Adicionar companyId apenas se fornecido
  if (companyId) {
    params.companyId = companyId
  }

  return api.get(`/api/1.0/users`, {
    params,
  })
}

export const getUser = (id) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.get(`/api/1.0/user/${id}`)
}

export const getUserSelf = () => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))

  return api.get(`/api/1.0/user/me`)
}

export const updateUser = (id, body) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.put(`/api/1.0/user/${id}`, body)
}

export const deleteUser = (id) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.delete(`/api/1.0/user/${id}`)
}

export const requestReset = (email) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.post('/api/1.0/user/password', { email })
}

export const putPassword = (body) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.put('/api/1.0/user/password', body)
}

export const putChangePassword = (idUser, body) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.put('/api/1.0/user/change/password/' + idUser, body)
}

export const postProfileImage = async (file) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return new Promise((resolve, reject) => {
    if (file) {
      const formData = new FormData()
      formData.append('profilePhoto', file[0])
      api.defaults.headers['Content-Type'] = 'multipart/form-data'
      api.post('/api/1.0/images', formData).then((responseFile) => {
        console.log(responseFile)
        delete api.defaults.headers['Content-Type']
        resolve(responseFile)
      })
    }
  })
}

export const postCompanyLogo = async (id, file) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return new Promise((resolve, reject) => {
    if (file) {
      const formData = new FormData()
      formData.append('file', file[0])
      api.defaults.headers['Content-Type'] = 'multipart/form-data'
      api.post(`/api/1.0/company/${id}/logo`, formData).then((responseFile) => {
        console.log(responseFile)
        delete api.defaults.headers['Content-Type']
        resolve(responseFile)
      })
    }
  })
}

export const getProfileImage = () => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.get('/api/1.0/images')
}

export const blockUser = (id, block) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.post(`/api/1.0/user/${id}/block`, { block })
}

export const approveUser = (id) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.post(`/api/1.0/user/${id}/reviewed`)
}

export const verifyToken = () => {
  const authData = secureLs.get('appc-auth-mkplace')
  if (!authData || !authData.token) {
    return Promise.reject(new Error('No token found'))
  }

  setAuthorizationHeader(authData)
  // Usar o endpoint de usuário atual para verificar se o token é válido
  return api.get('/api/1.0/user/me')
}

/* User API com campos de employee */
export const postUser = (body) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.post('/api/1.0/user', body)
}

// Buscar todos os grupos de usuários
export const getUserGroups = (page = 0, size = 100, text = '') => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.get(`/api/1.0/user-groups`, {
    params: { page, size, text },
  })
}

// Função para buscar grupo por ID
export const getGroupById = async (groupId) => {
  setAuthorizationHeader(secureLs.get('appc-auth-mkplace'))
  return api.get(`/api/1.0/user-groups/${groupId}`)
}
